import { useCallback, useMemo } from 'react';

import {
  ListValue,
  useItemsSearch,
  useKeyboardNavigation,
} from '@xemplo/stateful-list-core';

import { useValueTracker } from '../../utils/hooks/use-value-tracker';
import type { TagsSelectContextType } from '../../utils/types/stateful-list.types';

import type { TagsSelectProps } from './tags-select.types';

export function useTagsState(props: TagsSelectProps) {
  const {
    items,
    onChange,
    value,
    onSearchChange,
    onTagCreate,
    maxTagChar = 40,
    validCharRegex = /^[a-zA-Z0-9-_]+$/,
  } = props;
  const { renderItems, valueItems, valueItemMap, isNoResult, search, setSearch } =
    useItemsSearch({ items });

  const { selectedItems, isSelected, toggleItem } = useValueTracker({ value, onChange });

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      onSearchChange?.(value);
    },
    [onSearchChange, setSearch]
  );

  const handleToggleItem = useCallback(
    (value: ListValue) => toggleItem(value, valueItemMap),
    [toggleItem, valueItemMap]
  );

  // Patch valueItems to include the itemCallback for toggling selection
  // This allows each item to call toggleItem when clicked
  // or selected via keyboard navigation.
  const patchedValueItems = useMemo(
    () => valueItems.map((item) => ({ ...item, itemCallback: handleToggleItem })),
    [handleToggleItem, valueItems]
  );

  const { activeDescendantId, containerRef, onKeyDown } = useKeyboardNavigation({
    id: renderItems.id,
    items: patchedValueItems,
    useSearch: true,
    useSelectAll: false,
  });

  return useMemo(
    () =>
      ({
        items,
        valueItems,
        isNoResult,
        renderItems,
        selectedItems,
        activeDescendantId,
        containerRef,
        search,
        onKeyDown,
        toggleItem: handleToggleItem,
        isSelected,
        setSearch: handleSearchChange,
        useSearch: true,
        onTagCreate,
        maxTagChar,
        validCharRegex,
      } satisfies TagsSelectContextType),
    [
      activeDescendantId,
      containerRef,
      handleSearchChange,
      isNoResult,
      isSelected,
      items,
      maxTagChar,
      onKeyDown,
      onTagCreate,
      renderItems,
      search,
      selectedItems,
      handleToggleItem,
      validCharRegex,
      valueItems,
    ]
  );
}
